import { createSlice } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import {
  ActiveAgentTab,
  CommissionModelOption,
  PresetFilters,
  TagOption,
} from '../../types/searchFilters';
import { getSearchFilters } from './actions';
import { fetchAiRanking } from '../aiRanking/actions';
import { FilterTypes } from '../../enums/FilterTypes';
import { isBoolean, isNumber } from '../../utils/dataType';

function unwrapCookieBoolean(
  cookie: string | undefined,
  defaultValue: boolean,
) {
  if (cookie === 'true') return true;
  if (cookie === 'false') return false;
  return defaultValue;
}

type SearchFiltersState = {
  loading: boolean;
  error: unknown;
  presetFilters: PresetFilters | null;
  selectedCommissionModels: CommissionModelOption[];
  tags: TagOption[];
  excludeTags: TagOption[];
  priceLow: number | null;
  priceHigh: number | null;
  eligible: boolean;
  signRate: number | null;
  displayFormSubmissions: boolean;
  displayHomeBases: boolean;
  displayTransactions: boolean;
  smartSortOn: boolean;
  activeAgentTab: ActiveAgentTab;
  /**
   * Deal id of the most recent in-flight AI-ranking fetch. Used to ignore a
   * stale `fetchAiRanking.fulfilled` (from a deal the user swapped away from)
   * when deciding whether to auto-select the AI tab.
   */
  aiRankingDealId: string | null;
  filtersNeedsUpdating: boolean;
};

const initialState: SearchFiltersState = {
  loading: false,
  error: null,
  presetFilters: null,
  selectedCommissionModels: [],
  tags: [],
  excludeTags: [],
  eligible: true,
  priceLow: null,
  priceHigh: null,
  signRate: null,
  displayFormSubmissions: unwrapCookieBoolean(
    Cookies.get('displayFormSubmissions'),
    false,
  ),
  displayHomeBases: unwrapCookieBoolean(Cookies.get('displayHomeBases'), true),
  displayTransactions: unwrapCookieBoolean(
    Cookies.get('displayTransactions'),
    true,
  ),
  smartSortOn: false,
  activeAgentTab: 'standard',
  aiRankingDealId: null,
  filtersNeedsUpdating: false,
};

function isCommissionModelArray(
  value: unknown,
): value is CommissionModelOption[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item.label === 'string' && typeof item.value === 'number',
    )
  );
}

function isTagOptionArray(value: unknown): value is TagOption[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item.label === 'string' && typeof item.value === 'string',
    )
  );
}

const searchFiltersSlice = createSlice({
  name: 'searchFilters',
  initialState,
  reducers: {
    resetFilters(state) {
      state.eligible = true;
      state.priceHigh = null;
      state.priceLow = null;
      state.signRate = null;
      state.tags = [];
      state.excludeTags = [];
      state.selectedCommissionModels = [];
      state.filtersNeedsUpdating = true;
    },
    updateFilters(
      state: SearchFiltersState,
      {
        payload,
      }: {
        payload: {
          filterType: FilterTypes;
          value:
            | boolean
            | number
            | null
            | CommissionModelOption[]
            | TagOption[];
        };
      },
    ) {
      const { filterType, value } = payload;
      switch (filterType) {
        case FilterTypes.eligible:
          if (isBoolean(value)) state.eligible = value;
          break;
        case FilterTypes.priceHigh:
          if (isNumber(value) || value === null) state.priceHigh = value;
          break;
        case FilterTypes.priceLow:
          if (isNumber(value) || value === null) state.priceLow = value;
          break;
        case FilterTypes.selectedCommissionModels:
          if (isCommissionModelArray(value))
            state.selectedCommissionModels = value;
          break;
        case FilterTypes.signRate:
          if (isNumber(value) || value === null) state.signRate = value;
          break;
        case FilterTypes.tags:
          if (isTagOptionArray(value)) state.tags = value;
          break;
        case FilterTypes.excludeTags:
          if (isTagOptionArray(value)) state.excludeTags = value;
          break;
        default:
          break;
      }
      state.filtersNeedsUpdating = true;
    },
    fireSetFilters(state, action) {
      state.filtersNeedsUpdating = action.payload;
    },
    toggleFormSubmissions(state) {
      state.displayFormSubmissions = !state.displayFormSubmissions;
      Cookies.set('displayFormSubmissions', `${state.displayFormSubmissions}`);
      state.filtersNeedsUpdating = true;
    },
    toggleHomeBases(state) {
      state.displayHomeBases = !state.displayHomeBases;
      Cookies.set('displayHomeBases', `${state.displayHomeBases}`);
      state.filtersNeedsUpdating = true;
    },
    toggleTransactions(state) {
      state.displayTransactions = !state.displayTransactions;
      Cookies.set('displayTransactions', `${state.displayTransactions}`);
      state.filtersNeedsUpdating = true;
    },
    toggleSmartSortedAgents(state) {
      state.smartSortOn = !state.smartSortOn;
    },
    setActiveAgentTab(state, { payload }: { payload: ActiveAgentTab }) {
      state.activeAgentTab = payload;
    },
  },
  extraReducers: (builder) => {
    // Default the tab from the ranking response itself (not a component
    // effect) so the "AI Ranked" tab is pre-selected without a render race.
    builder.addCase(fetchAiRanking.pending, (state, { meta }) => {
      state.activeAgentTab = 'standard';
      state.aiRankingDealId = String(meta.arg);
    });
    builder.addCase(fetchAiRanking.fulfilled, (state, { payload, meta }) => {
      // Only flip to the AI tab for the deal whose fetch is still current: a
      // late fulfilled for a deal the user already swapped away from must not
      // reselect the AI tab (see aiRankingDealId above).
      if (String(meta.arg) !== state.aiRankingDealId) return;
      if (
        (payload?.experimentArm === 'show-ai-ranking' ||
          payload?.experimentArm === 'show-match-score-ranking') &&
        (payload.ranking?.length ?? 0) > 0
      ) {
        state.activeAgentTab = 'ai';
      }
    });
    builder.addCase(getSearchFilters.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getSearchFilters.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.presetFilters = payload;
    });
    builder.addCase(getSearchFilters.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    });
  },
});

export const {
  updateFilters,
  resetFilters,
  fireSetFilters,
  setActiveAgentTab,
  toggleFormSubmissions,
  toggleHomeBases,
  toggleSmartSortedAgents,
  toggleTransactions,
} = searchFiltersSlice.actions;

export default searchFiltersSlice.reducer;
